import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";

import "./SignUpForm.css";

import service from "../../services/service";
import endpoints from "../../services/endpoints";

import { setCredentials } from "../../features/auth/authSlice";

export default function SignupForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const googleButtonRef = useRef(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCreatePassword, setShowCreatePassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [googleLoading, setGoogleLoading] = useState(false);

  
  const handleSignup = async (e) => {
    e.preventDefault();

   
    if (!agreedToTerms) {
      return;
    }
    if (createPassword !== confirmPassword) {
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        fullName,
        email,
        password: createPassword,
        confirmPassword,
        roleType: "USER",
      };

      const res = await service.post(endpoints.auth.signup, payload);

      console.log("SIGNUP RESPONSE", res?.data);

      // Redirect to Verify Email page
      navigate("/verify-email", {
        state: {
          email,
          purpose: "register",
        },
      });
    } catch (error) {
      console.log("SIGNUP ERROR", error);
    } finally {
      setIsLoading(false);
    }
  };

  /* GOOGLE LOGIN */
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setGoogleLoading(true);
      const payload = {
        token: credentialResponse.credential,
      };

      const res = await service.post(endpoints.auth.googleLogin, payload);
      const responseData = res?.data?.data;

      // SAVE TO REDUX
      dispatch(
        setCredentials({
          accessToken: responseData?.accessToken,

          refreshToken: responseData?.refreshToken,

          user: responseData?.user,
        }),
      );

      // REDIRECT
      navigate("/home");
    } catch (error) {
      console.log("GOOGLE LOGIN ERROR", error);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="signup-form-wrapper">
      <div className="signup-heading">
        <h1>Create Your Account</h1>
        <p>Enter details to create an account</p>
      </div>

      <form className="signup-form" onSubmit={handleSignup}>
        <div className="signup-form-group">
          <label>Full Name</label>

          <input
            type="text"
            placeholder="Enter full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>

        <div className="signup-form-group">
          <label>Email ID</label>

          <input
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="signup-form-group">
          <label>Create Password</label>

          <div className="signup-password-field">
            <input
              type={showCreatePassword ? "text" : "password"}
              placeholder="••••••••"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              required
            />

            <button
              type="button"
              className="signup-eye-btn"
              onClick={() => setShowCreatePassword(!showCreatePassword)}
            >
              {showCreatePassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="signup-form-group">
          <label>Confirm Password</label>

          <div className="signup-password-field">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <button
              type="button"
              className="signup-eye-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="signup-terms-checkbox">
          <input
            type="checkbox"
            id="terms"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
          />

          <label htmlFor="terms">
            By continuing you&apos;re accepting our{" "}
            <span className="signup-terms-link">Terms and Conditions</span>
          </label>
        </div>

        <button
          type="submit"
          className="signup-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? "Creating..." : "Create Account"}
        </button>
      </form>

      <div className="signup-divider">
        <div></div>
        <span>or</span>
        <div></div>
      </div>

      <button
        type="button"
        className="signup-google-btn"
        disabled={googleLoading}
        onClick={() => {
          const googleButton =
            googleButtonRef.current?.querySelector("div[role=button]");

          if (googleButton) {
            googleButton.click();
          }
        }}
      >
        {googleLoading ? "Please wait..." : "Continue with Google"}
      </button>

      <div
        ref={googleButtonRef}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => {
            console.log("Google Login Failed");
          }}
        />
      </div>

      <div className="signup-login-link">
        <p>
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
}
